import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { fetchJson } from "../api";

// Base per i file (video mp4 / m3u8). Usa REACT_APP_API_URL e rimuove "/api".
const FILE_ORIGIN = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api\/?$/, "");

/* ----------------- Utils ----------------- */
function formatTcProgressive(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}:${d.slice(2)}`;
  if (d.length <= 6) return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4)}`;
  return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4, 6)}:${d.slice(6)}`;
}
function normalizeTcFull(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8).padEnd(8, "0");
  return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4, 6)}:${d.slice(6, 8)}`;
}

const FPS = 25;
const toSMPTE = (sec, fps = FPS) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  const ff = String(Math.floor((sec - s) * fps)).padStart(2, "0");
  return `${hh}:${mm}:${ss}:${ff}`;
};
const fromSMPTE = (smpte, fps = FPS) => {
  const m = /^(\d{2}):(\d{2}):(\d{2}):(\d{2})$/.exec(smpte || "");
  if (!m) return 0;
  const [, hh, mm, ss, ff] = m.map(Number);
  return hh * 3600 + mm * 60 + ss + ff / fps;
};

/* -------- Normalizzazione note -------- */
function normalizeNote(row) {
  if (!row) return null;
  const tcSecondsRaw = row.tc_seconds ?? row.tcSeconds;
  const hasTcSeconds = tcSecondsRaw !== null && tcSecondsRaw !== undefined && tcSecondsRaw !== "";
  const tcSeconds = hasTcSeconds ? Number(tcSecondsRaw) || 0 : null;
  const tcSmpte = row.tc_smpte ?? row.timecode ?? (tcSeconds != null ? toSMPTE(tcSeconds) : null);
  return {
    id: row.id,
    tcSeconds,
    tcSmpte,
    fps: row.fps ?? null,
    text: row.text ?? row.note_text ?? "",
    author: row.author ?? row.author_label ?? "",
    createdAt: row.created_at ?? row.createdAt ?? null,
    resolved: !!row.resolved,
  };
}
function toApiNote(n) {
  return {
    id: n.id,
    tc_seconds: n.tcSeconds,
    tc_smpte: n.tcSmpte,
    text: n.text,
    fps: n.fps ?? FPS,
    author_label: n.author ?? null,
    resolved: n.resolved,
  };
}

/* ----------------- Styled ----------------- */
const Shell = styled(Box)(() => ({
  minHeight: "100vh",
  background: "#0a0a0f",
}));

const PlayerPanel = styled(Paper)(() => ({
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid rgba(212,165,80,0.12)",
  background: "#0d0d12",
  boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
}));

const NotesPanel = styled(Paper)(() => ({
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.07)",
  background: "#0f0f16",
  overflow: "hidden",
  boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
}));

const Row = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "14px 16px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  transition: "background .15s ease",
  "&:hover": { background: "rgba(212,165,80,0.03)" },
  "&:hover .note-actions": { opacity: 1 },
}));

const TcButton = styled(Button)(() => ({
  minWidth: 0,
  width: "100%",
  padding: "5px 9px",
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: 0.5,
  textTransform: "none",
  justifyContent: "flex-start",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  background: "rgba(212,165,80,0.08)",
  border: "1px solid rgba(212,165,80,0.2)",
  color: "#d4a550",
  "&:hover": {
    background: "rgba(212,165,80,0.15)",
    border: "1px solid rgba(212,165,80,0.4)",
  },
}));

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/* ----------------- Row component ----------------- */
function NoteRow({
  note,
  onSeek,
  onDelete,
  onSaveText,
  readOnly,
}) {
  const [draft, setDraft] = useState(note.text || "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Se arriva un update dall'esterno (es: refresh), riallineo solo se non sto editando.
    if (!dirty) setDraft(note.text || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id, note.text]);

  return (
    <Row>
      {/* Top: timecode + data + cestino */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TcButton
            variant="text"
            onClick={() => onSeek(note.tcSeconds || 0)}
            startIcon={<PlayArrowRoundedIcon />}
          >
            {note.tcSmpte}
          </TcButton>
          {note.createdAt && (
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.25)", whiteSpace: "nowrap" }}>
              {new Date(note.createdAt).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Box className="note-actions" sx={{ opacity: 0, transition: "opacity .12s ease" }}>
          <Tooltip title="Elimina">
            <span>
              <IconButton size="small" color="error" onClick={() => onDelete(note.id)} disabled={readOnly}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {/* Bottom: testo nota */}
      <Box sx={{
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "8px",
        background: "rgba(255,255,255,0.03)",
        px: 1.5, py: 1,
        minHeight: 60,
        transition: "border-color .2s",
        "&:focus-within": { borderColor: "rgba(212,165,80,0.25)" },
      }}>
        <InputBase
          multiline fullWidth
          value={draft}
          readOnly={readOnly}
          placeholder="Scrivi…"
          onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
          onBlur={() => {
            if (!dirty) return;
            const next = (draft || "").trimEnd();
            setDirty(false);
            if (next !== (note.text || "")) onSaveText(note.id, next);
          }}
          sx={{ fontSize: 13.5, lineHeight: 1.55, color: "#f0ece4" }}
        />
      </Box>
    </Row>
  );
}

/* ----------------- Component ----------------- */
export default function ReviewPlayerPage() {
  const videoRef = useRef(null);
  const playerWrapRef = useRef(null);
  const { id: reviewSlug } = useParams();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Public mode: quando l'utente non è loggato (link di visione pubblico)
  // Serve solo a nascondere strumenti “admin” (export), non influisce su password/note.
  const isPublicMode = !window.localStorage.getItem("token");

  // AUTH
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [videoSrc, setVideoSrc] = useState("");
  const [reviewerToken, setReviewerToken] = useState(null);

  // NOTE
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [tc, setTc] = useState({ seconds: 0, smpte: "00:00:00:00" });
  const [tcOffset, setTcOffset] = useState(0); // offset da start_timecode del file
  const [notes, setNotes] = useState([]);
  const [rawNotes, setRawNotes] = useState([]);
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null, text: "" });

  // META
  const [prodName, setProdName] = useState("");
  const [episode, setEpisode] = useState(null);

  // UI
  const [errMsg, setErrMsg] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  // UI sizing: manteniamo il pannello note agganciato al box video+timeline (stile Frame.io)
  const [playerHeight, setPlayerHeight] = useState(null);

  // REVIEW ID (numerico se/quando disponibile)
  const [reviewNumericId, setReviewNumericId] = useState(null);

  // CONSIDERAZIONI GENERALI (senza timecode)
  const [generalText, setGeneralText] = useState("");
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [generalDirty, setGeneralDirty] = useState(false);

  // Player state (timeline)
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [seeking, setSeeking] = useState(false);

  useLayoutEffect(() => {
    if (!authed || !isMdUp) return;
    const el = playerWrapRef.current;
    if (!el) return;

    const update = () => {
      const h = Math.round(el.getBoundingClientRect().height || 0);
      setPlayerHeight(h > 0 ? h : null);
    };

    // doppio tick per assicurare layout stabile (video metadata / fonts)
    update();
    requestAnimationFrame(() => update());
    let ro;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener("resize", update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", update);
    };
  }, [authed, isMdUp, videoSrc, duration]);

  // Rid usato per GET/POST (lo slug va bene), PUT/DELETE usano endpoint senza reviewId
  const rid = reviewNumericId || reviewSlug;

  const { generalNotes, timecodeNotes } = useMemo(() => {
    const g = [];
    const t = [];
    for (const n of notes) {
      // "Considerazioni generali" vengono salvate come una nota speciale a TC 00:00:00:00
      // (serve perché alcuni schemi DB richiedono tc_smpte/tc_seconds NOT NULL).
      const isGeneral =
        String(n?.author || "").trim().toLowerCase() === "generale" &&
        (n?.tcSeconds == null || Number(n.tcSeconds) === 0);

      if (isGeneral || n?.tcSeconds == null || !n?.tcSmpte) g.push(n);
      else t.push(n);
    }
    // generali: più recenti in alto
    g.sort((a, b) => {
      const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });
    // timecode: in ordine di TC
    t.sort((a, b) => (a.tcSeconds ?? 0) - (b.tcSeconds ?? 0));
    return { generalNotes: g, timecodeNotes: t };
  }, [notes]);

  // In questa fase vogliamo UN solo commento generale (testo lungo non legato a TC)
  const generalNote = useMemo(() => (generalNotes && generalNotes.length ? generalNotes[0] : null), [generalNotes]);

  useEffect(() => {
    // sync testo solo se l'utente non sta editando
    if (!generalDirty) {
      setGeneralText(generalNote?.text || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalNote?.id]);

  const title = useMemo(() => {
    if (!prodName) return "Revisione";
    if (episode == null) return prodName;
    return `${prodName} — Ep ${episode}`;
  }, [prodName, episode]);

  /* ------------ Data fetching ------------ */
  const loadNotes = useCallback(async () => {
    if (!authed || !rid) return;
    const data = await fetchJson(`/revisions/${rid}/notes`);
    const arr = Array.isArray(data) ? data : Array.isArray(data?.notes) ? data.notes : [];
    setRawNotes(arr);
    setNotes(arr.map(normalizeNote).filter(Boolean));
  }, [authed, rid]);

  const loadProductionName = useCallback(async (productionId) => {
    try {
      if (!productionId) return;
      const p = await fetchJson(`/productions/${productionId}`);
      const name = p?.name || p?.title || p?.code || `Produzione ${productionId}`;
      setProdName(name);
    } catch (e) {
      console.warn("Impossibile leggere nome produzione:", e);
    }
  }, []);

  const extractReviewIdFromRow = (row) => {
    if (!row || typeof row !== "object") return null;
    const candidates = [
      "review_id",
      "reviewId",
      "revision_id",
      "revisionId",
      "reviews_id",
      "rev_id",
      "revId",
      "review",
      "revision",
    ];
    for (const key of candidates) {
      if (row[key] != null && /^\d+$/.test(String(row[key]))) return Number(row[key]);
    }
    for (const k of Object.keys(row)) {
      const v = row[k];
      if (v != null && /^\d+$/.test(String(v))) {
        const kk = k.toLowerCase();
        if ((kk.includes("review") || kk.includes("revision")) && kk.includes("id")) {
          return Number(v);
        }
      }
    }
    return null;
  };

  const resolveNumericIdFromNotes = useCallback(async () => {
    try {
      const data = await fetchJson(`/revisions/${reviewSlug}/notes`);
      const arr = Array.isArray(data?.notes) ? data.notes : Array.isArray(data) ? data : [];
      setRawNotes(arr);
      const got = extractReviewIdFromRow(arr[0]);
      if (got) setReviewNumericId(got);
    } catch {
      /* noop */
    }
  }, [reviewSlug]);

  useEffect(() => {
    if (!authed || !rid) return;
    loadNotes().catch((err) => {
      console.error("Errore fetch notes:", err);
      setErrMsg("Impossibile caricare le note.");
      setSnack({ open: true, severity: "error", message: "Caricamento note fallito." });
    });
  }, [authed, rid, loadNotes]);

  // Player events: duration/timeupdate
  useEffect(() => {
    if (!authed) return;
    const v = videoRef.current;
    if (!v) return;

    const onLoaded = () => {
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
    };
    const onTime = () => {
      if (!seeking) setCurrentTime(v.currentTime || 0);
    };
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
    };
  }, [authed, seeking]);

  // Pausa video -> apre dialog nuova nota
  useEffect(() => {
    if (!authed) return;
    const v = videoRef.current;
    const onPause = () => {
      const sec = v?.currentTime || 0;
      setTc({ seconds: sec, smpte: toSMPTE(sec + tcOffset) });
      setNoteOpen(true);
    };
    v?.addEventListener("pause", onPause);
    return () => v?.removeEventListener("pause", onPause);
  }, [authed]);

  /* ------------ Azioni ------------ */
  const doAuth = async () => {
    try {
      const data = await fetchJson(`/revisions/${reviewSlug}/auth`, {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      if (!data?.reviewer_token) throw new Error("Nessun reviewer_token");
      setReviewerToken(data.reviewer_token);
      setAuthed(true);
      setErrMsg(null);
      setSnack({ open: true, severity: "success", message: "Accesso effettuato" });

      setEpisode(data?.episode ?? null);
      // URL video: supporta hls_url (nuovo) o file_url (legacy). Se è relativo, prependiamo FILE_ORIGIN.
      const rawVideoUrl = data?.hls_url || data?.file_url || data?.fileUrl || data?.url || "";
      if (rawVideoUrl) {
        setVideoSrc(rawVideoUrl.startsWith("http") ? rawVideoUrl : `${FILE_ORIGIN}${rawVideoUrl}`);
      } else {
        setVideoSrc("");
      }
      // Leggi start_timecode e converti in secondi
      if (data?.start_timecode) {
        const parts = data.start_timecode.split(':').map(Number);
        if (parts.length >= 3) {
          const offsetSec = parts[0]*3600 + parts[1]*60 + parts[2];
          setTcOffset(offsetSec);
        }
      }
      await loadProductionName(data?.production_id);

      // prova a risolvere l'ID numerico (non blocca se fallisce)
      await resolveNumericIdFromNotes();
      await loadNotes();
    } catch (e) {
      console.error("Auth fallita:", e);
      setErrMsg("Password errata o link scaduto.");
      setSnack({ open: true, severity: "error", message: "Autenticazione fallita" });
    }
  };

  const seekTo = useCallback((sec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = clamp(Number(sec) || 0, 0, Number.isFinite(v.duration) ? v.duration : sec);
    v.pause();
    setCurrentTime(v.currentTime || 0);
  }, []);

  const sendNote = async () => {
    try {
      const base = {
        tcSeconds: tc.seconds,
        tcSmpte: tc.smpte,
        text: noteText.trim(),
        resolved: false,
        fps: FPS,
      };
      const optimisticId = `tmp-${Date.now()}`;
      const optimistic = normalizeNote({ id: optimisticId, ...base });
      setNotes((prev) => [...prev, optimistic]);

      const payload = { ...toApiNote({ id: undefined, ...base }), reviewer_token: reviewerToken };
      const created = await fetchJson(`/revisions/${rid}/notes`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const norm = normalizeNote(created);
      setNotes((prev) => prev.map((n) => (n.id === optimisticId ? norm : n)));

      setNoteText("");
      setNoteOpen(false);
      setErrMsg(null);
      setSnack({ open: true, severity: "success", message: "Nota salvata" });
    } catch (err) {
      console.error("Errore invio nota:", err);
      setNotes((prev) => prev.filter((n) => !String(n.id).startsWith("tmp-")));
      setSnack({ open: true, severity: "error", message: "Errore salvataggio nota" });
    }
  };

  const sendGeneralNote = async () => {
    const txt = (generalText || "").trim();
    if (!txt) return;
    // Alcune installazioni hanno tc_smpte/tc_seconds NOT NULL su review_notes.
    // Per mantenere "Considerazioni generali" separata dalla lista timecoded,
    // salviamo una nota speciale a TC 00:00:00:00 con author_label="Generale".
    const generalPayload = {
      text: txt,
      tc_smpte: "00:00:00:00",
      tc_seconds: 0,
      fps: FPS,
      author_label: "Generale",
      reviewer_token: reviewerToken,
    };
    try {
      setSavingGeneral(true);
      // Se esiste già una considerazione generale, aggiorniamo quella (single comment)
      if (generalNote?.id) {
        const updated = await fetchJson(`/revisions/notes/${generalNote.id}`, {
          method: "PUT",
          // PUT supporta solo text/author/action: inviamo i campi minimi
          body: JSON.stringify({ text: txt, author_label: "Generale", reviewer_token: reviewerToken }),
        });
        const norm = normalizeNote(updated);
        setNotes((prev) => prev.map((n) => (n.id === generalNote.id ? norm : n)));
      } else {
        // altrimenti creiamo una nuova nota senza TC
        const created = await fetchJson(`/revisions/${rid}/notes`, {
          method: "POST",
          body: JSON.stringify(generalPayload),
        });
        const norm = normalizeNote(created);
        setNotes((prev) => [norm, ...prev]);
      }

      setGeneralDirty(false);
      setSnack({ open: true, severity: "success", message: "Considerazioni salvate" });
    } catch (e) {
      console.error("Errore invio considerazioni:", e);
      setSnack({ open: true, severity: "error", message: "Errore salvataggio considerazioni" });
    } finally {
      setSavingGeneral(false);
    }
  };

  // PUT backend: aggiorna SOLO testo/autore (non TC/resolved)
  const updateNote = async (id, patch) => {
    try {
      const current = notes.find((n) => n.id === id);
      if (!current) return;

      const merged = { ...current, ...patch };
      setNotes((prev) => prev.map((n) => (n.id === id ? merged : n)));

      const payload = {
        text: patch.text !== undefined ? patch.text : current.text,
        author_label: patch.author !== undefined ? patch.author : current.author,
      };

      if (payload.text === current.text && (payload.author_label ?? null) === (current.author ?? null)) {
        return; // niente da aggiornare lato backend
      }

      const upd = await fetchJson(`/revisions/notes/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      const norm = normalizeNote({
        id: upd.id,
        tc_seconds: upd.tc_seconds,
        tc_smpte: upd.timecode,
        fps: upd.fps,
        text: upd.note_text ?? upd.text,
        author: upd.author,
        created_at: upd.created_at,
      });
      setNotes((prev) => prev.map((n) => (n.id === id ? norm : n)));
    } catch (err) {
      console.error("Errore update nota:", err);
      setSnack({ open: true, severity: "error", message: "Errore aggiornamento nota" });
      loadNotes().catch(() => {});
    }
  };

  const deleteNote = async (id) => {
    try {
      setNotes((prev) => prev.filter((n) => n.id !== id));
      await fetchJson(`/revisions/notes/${id}`, {
        method: "DELETE",
        body: JSON.stringify({ reviewer_token: reviewerToken }),
      });
      setSnack({ open: true, severity: "success", message: "Nota eliminata" });
    } catch (err) {
      console.error("Errore eliminazione nota:", err);
      setSnack({ open: true, severity: "error", message: "Errore eliminazione nota" });
      loadNotes().catch(() => {});
    }
  };

  // Export PDF semplice (tenuto solo per admin)
  const exportPDF = () => {
    const rows = timecodeNotes.map((n) => `${n.tcSmpte} — ${n.text?.replace(/\n/g, " ") || ""}`);
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    const css = `
      <style>
        body { font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111; margin:32px; }
        h1 { font-size:20px; margin:0 0 16px; }
        .meta { color:#666; margin-bottom:20px; }
        .row { padding:8px 0; border-bottom:1px solid #eee; white-space:pre-wrap; }
        .tc { font-weight:700; display:inline-block; width:120px; }
        .txt { display:inline-block; vertical-align:top; }
      </style>
    `;
    const header = `<h1>Note revisione</h1><div class="meta">${title}</div>`;
    const body = rows
      .map((r) => {
        const [tcc, ...rest] = r.split(" — ");
        const txt = rest.join(" — ");
        return `<div class="row"><span class="tc">${tcc}</span><span class="txt">${txt}</span></div>`;
      })
      .join("");
    w.document.write(`<!doctype html><html><head>${css}</head><body>${header}${body}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  // Marks timeline (limito per performance)
  const timelineMarks = useMemo(() => {
    const maxMarks = 40;
    const arr = timecodeNotes
      .filter((n) => Number.isFinite(n.tcSeconds))
      .slice(0, 250) // cap di sicurezza
      .map((n) => ({ value: n.tcSeconds, label: "" }));
    if (arr.length <= maxMarks) return arr;
    const step = Math.ceil(arr.length / maxMarks);
    return arr.filter((_, i) => i % step === 0);
  }, [timecodeNotes]);

  /* ------------ Render ------------ */
  if (!reviewSlug) return <Box sx={{ p: 3 }}>Link non valido</Box>;

  if (!authed) {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: "#0a0a0f",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage: "linear-gradient(rgba(212,165,80,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(212,165,80,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
        <Box sx={{
          position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)",
          width: 600, height: 600, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(212,165,80,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <Box sx={{ position: "relative", width: "100%", maxWidth: 420, zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mb: 2 }}>
              {["J","K","L"].map((k) => (
                <Box key={k} sx={{
                  width: 36, height: 36, borderRadius: "8px",
                  border: "1.5px solid rgba(212,165,80,0.5)",
                  background: "rgba(212,165,80,0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#d4a550", fontWeight: 700, fontSize: 15, letterSpacing: 1,
                }}>
                  {k}
                </Box>
              ))}
            </Box>
            <Typography sx={{
              color: "rgba(255,255,255,0.35)", fontSize: 11,
              letterSpacing: 4, textTransform: "uppercase",
            }}>
              Visione riservata
            </Typography>
          </Box>
          <Box sx={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(212,165,80,0.15)",
            borderRadius: "16px",
            p: 3.5,
          }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 22, mb: 0.5 }}>
              Accesso alla visione
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13, mb: 3 }}>
              Inserisci la password ricevuta per accedere al materiale.
            </Typography>
            <Box sx={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              px: 2, py: 0.75, mb: 2,
              transition: "border-color .2s",
              "&:focus-within": { borderColor: "rgba(212,165,80,0.5)" },
            }}>
              <InputBase
                fullWidth
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doAuth(); }}
                sx={{
                  color: "#fff", fontSize: 15,
                  "& input::placeholder": { color: "rgba(255,255,255,0.25)" },
                }}
              />
            </Box>
            <Button
              fullWidth
              onClick={doAuth}
              sx={{
                background: "linear-gradient(135deg, #d4a550, #b8892f)",
                color: "#0a0a0f", fontWeight: 800, fontSize: 15,
                borderRadius: "10px", py: 1.4,
                textTransform: "none", letterSpacing: 0.5,
                boxShadow: "0 4px 20px rgba(212,165,80,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #e0b660, #c49435)" },
              }}
            >
              Accedi
            </Button>
            {errMsg && (
              <Typography sx={{ mt: 2, color: "#ff6b6b", fontSize: 13, textAlign: "center" }}>
                {errMsg}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Shell sx={{ p: { xs: 1.5, md: 3 } }}>


      <Box sx={{
        maxWidth: 1560, mx: "auto",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 400px" },
        gap: 2.5, alignItems: "start",
      }}>
        {/* LEFT — Player + Considerazioni */}
        <Box ref={playerWrapRef} sx={{ position: { md: "sticky" }, top: { md: 16 }, alignSelf: "start" }}>
          <PlayerPanel elevation={0}>
            {/* Ciak header */}
            <Box sx={{
              px: 2.5, py: 1.75,
              background: "#080810",
              borderBottom: "1px solid rgba(212,165,80,0.1)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              minHeight: 56,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                {/* REC dot */}
                <Box sx={{
                  width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                  background: "#e05252",
                  boxShadow: "0 0 6px rgba(224,82,82,0.8)",
                  animation: "recPulse 2s ease-in-out infinite",
                  "@keyframes recPulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.3 },
                  },
                }} />
                <Typography sx={{
                  color: "#fff", fontWeight: 700, fontSize: 15,
                  letterSpacing: 0.1, whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {prodName || "Produzione"}
                </Typography>
                {episode != null && (
                  <Box sx={{
                    px: 1.25, py: 0.3, borderRadius: "5px", flexShrink: 0,
                    background: "rgba(212,165,80,0.12)",
                    border: "1px solid rgba(212,165,80,0.2)",
                  }}>
                    <Typography sx={{ color: "#d4a550", fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                      Ep {episode}
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: 10, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", flexShrink: 0 }}>
                  · VISIONE RISERVATA
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, ml: 2 }}>
                {["J","K","L"].map((k) => (
                  <Box key={k} sx={{
                    width: 20, height: 20, borderRadius: "4px",
                    border: "1px solid rgba(212,165,80,0.2)",
                    background: "rgba(212,165,80,0.05)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(212,165,80,0.4)", fontWeight: 700, fontSize: 9,
                  }}>{k}</Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ position: "relative", background: "#000" }}>
              <video
                ref={videoRef}
                controls
                style={{ width: "100%", display: "block", background: "#000" }}
                src={videoSrc}
              />
            </Box>
            <Box sx={{ px: 2.5, pt: 1.5, pb: 2, background: "#0d0d12" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ color: "#d4a550", fontSize: 12, fontWeight: 700, fontFamily: "monospace", letterSpacing: 1 }}>
                  {toSMPTE(currentTime + tcOffset)}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "monospace" }}>
                  {toSMPTE(duration)}
                </Typography>
              </Box>
              <Slider
                size="small"
                value={clamp(currentTime, 0, duration || 0)}
                min={0} max={Math.max(0, duration || 0)} step={0.04}
                marks={timelineMarks}
                onChange={(_, v) => { setSeeking(true); setCurrentTime(v); }}
                onChangeCommitted={(_, v) => { setSeeking(false); seekTo(v); }}
                sx={{
                  color: "#d4a550",
                  "& .MuiSlider-thumb": {
                    width: 14, height: 14, background: "#d4a550",
                    boxShadow: "0 0 0 3px rgba(212,165,80,0.2)",
                    "&:hover": { boxShadow: "0 0 0 5px rgba(212,165,80,0.3)" },
                  },
                  "& .MuiSlider-rail": { background: "rgba(255,255,255,0.1)", height: 3 },
                  "& .MuiSlider-track": { height: 3 },
                  "& .MuiSlider-mark": { background: "rgba(212,165,80,0.5)", width: 2, height: 8 },
                }}
              />
              {timecodeNotes.length > 0 && (
                <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: 11, mt: 0.5 }}>
                  {timecodeNotes.length} annotazioni sulla timeline
                </Typography>
              )}
            </Box>
          </PlayerPanel>

          {/* Considerazioni generali */}
          <Box sx={{
            mt: 2, borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.07)",
            background: "#0f0f16", overflow: "hidden",
          }}>
            <Box sx={{
              px: 2.5, py: 1.5,
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              display: "flex", alignItems: "center", gap: 1,
            }}>
              <Box sx={{ width: 3, height: 16, borderRadius: 2, background: "rgba(212,165,80,0.4)" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 1.5 }}>
                Considerazioni generali
              </Typography>
            </Box>
            <Box sx={{ px: 2.5, py: 2 }}>
              <Box sx={{
                border: "1px solid rgba(255,255,255,0.07)", borderRadius: "8px",
                background: "rgba(255,255,255,0.02)", px: 1.5, py: 1,
                transition: "border-color .2s",
                "&:focus-within": { borderColor: "rgba(212,165,80,0.3)" },
              }}>
                <InputBase
                  multiline fullWidth minRows={3} maxRows={10}
                  placeholder="Commenti generali non legati a un punto specifico del montato…"
                  value={generalText}
                  onChange={(e) => { setGeneralText(e.target.value); setGeneralDirty(true); }}
                  onBlur={() => { if (!generalDirty) return; sendGeneralNote(); }}
                  sx={{ fontSize: 14, lineHeight: 1.5, color: "#f0ece4" }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25 }}>
                <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: 11 }}>
                  {generalNote?.createdAt ? `Salvato: ${new Date(generalNote.createdAt).toLocaleString()}` : "Non ancora salvato"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {generalNote?.id && (
                    <Tooltip title="Elimina">
                      <IconButton size="small" onClick={() => setConfirmDel({ open: true, id: generalNote.id, text: generalNote.text })}>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.3)" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    disabled={savingGeneral || !(generalText || "").trim()}
                    onClick={async () => {
                      await sendGeneralNote();
                      try {
                        await fetchJson(`/revisions/${reviewSlug}/end`, { method: "POST" });
                        setSnack({ open: true, severity: "success", message: "Visione completata. Il coordinatore è stato notificato." });
                      } catch(e) {
                        console.warn("Notifica fine visione fallita:", e);
                      }
                    }}
                    sx={{
                      background: "linear-gradient(135deg, #d4a550, #b8892f)",
                      color: "#0a0a0f",
                      borderRadius: "8px", textTransform: "none", fontWeight: 800,
                      fontSize: 12, px: 2.5,
                      boxShadow: "0 2px 12px rgba(212,165,80,0.25)",
                      "&:hover": { background: "linear-gradient(135deg, #e0b660, #c49435)" },
                      "&:disabled": { opacity: 0.3, background: "rgba(212,165,80,0.15)", color: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    Salva e termina visione
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* RIGHT — Notes */}
        <NotesPanel
          elevation={0}
          sx={{
            position: { md: "sticky" }, top: { md: 16 }, alignSelf: "start",
            display: "flex", flexDirection: "column",
            height: { md: playerHeight ? `${playerHeight}px` : "auto" },
            maxHeight: { md: playerHeight ? `${playerHeight}px` : "none" },
            overflow: { md: "hidden" },
          }}
        >
          <Box sx={{
            px: 2, py: 1.5,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 3, height: 18, borderRadius: 2, background: "linear-gradient(180deg, #d4a550, #b8892f)" }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>Note</Typography>
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                  {timecodeNotes.length} con timecode
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{
                px: 1.25, py: 0.25, borderRadius: "6px",
                background: "rgba(212,165,80,0.12)", border: "1px solid rgba(212,165,80,0.2)",
                color: "#d4a550", fontSize: 12, fontWeight: 700,
              }}>
                {timecodeNotes.length}
              </Box>
              {!isPublicMode && (
                <Tooltip title="Export PDF">
                  <IconButton size="small" onClick={exportPDF} sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "#d4a550" } }}>
                    <DownloadRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Nuova nota manuale">
                <IconButton
                  size="small"
                  onClick={() => {
                    const v = videoRef.current;
                    const sec = v?.currentTime || 0;
                    setTc({ seconds: sec, smpte: toSMPTE(sec + tcOffset) });
                    setNoteOpen(true);
                  }}
                  sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: "#d4a550" } }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={{
            flex: 1, overflowY: "auto", background: "#0f0f16",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(212,165,80,0.2) transparent",
            "&::-webkit-scrollbar": { width: 3 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "rgba(212,165,80,0.2)", borderRadius: 2 },
          }}>
            {timecodeNotes.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: 13 }}>Nessuna nota ancora.</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.15)", fontSize: 12, mt: 0.5 }}>
                  Metti in pausa il video per aggiungerne una.
                </Typography>
              </Box>
            ) : (
              timecodeNotes.map((n, i) => (
                <NoteRow
                  key={n.id ?? `${n.tcSmpte}-${i}`}
                  note={n} readOnly={false}
                  onSeek={(sec) => seekTo(sec)}
                  onDelete={(id) => setConfirmDel({ open: true, id, text: n.text?.slice(0, 80) || "" })}
                  onSaveText={(id, text) => updateNote(id, { text })}
                />
              ))
            )}
          </Box>
        </NotesPanel>
      </Box>

      {/* Dialog nuova nota */}
      <Dialog open={noteOpen} onClose={() => setNoteOpen(false)} fullWidth maxWidth="sm" fullScreen={!isMdUp}>
        <DialogTitle sx={{ fontWeight: 950 }}>Nuova nota</DialogTitle>
        <DialogContent sx={{ pt: 2.25 }}>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>Timecode</Typography>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1.25, py: 0.5 }}>
                <InputBase
                  fullWidth value={tc.smpte} placeholder="00:00:00:00"
                  onChange={(e) => { const smpte = formatTcProgressive(e.target.value); setTc({ seconds: fromSMPTE(smpte), smpte }); }}
                  onBlur={(e) => { const smpte = normalizeTcFull(e.target.value); setTc({ seconds: fromSMPTE(smpte), smpte }); }}
                />
              </Box>
            </Box>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, background: "rgba(255,255,255,0.04)", px: 1.25, py: 0.75 }}>
              <InputBase
                autoFocus multiline fullWidth minRows={4}
                placeholder="Scrivi la modifica richiesta…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                sx={{ fontSize: 14.5, lineHeight: 1.45 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setNoteOpen(false)} sx={{ textTransform: "none" }}>Annulla</Button>
          <Button variant="contained" onClick={sendNote} disabled={!noteText.trim()} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            Salva nota
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={confirmDel.open} onClose={() => setConfirmDel({ open: false, id: null, text: "" })}>
        <DialogTitle sx={{ fontWeight: 950 }}>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>Sei sicuro di voler eliminare questa nota?</Typography>
          {confirmDel.text && (
            <Box sx={{ mt: 1.25, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.default" }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{confirmDel.text}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel({ open: false, id: null, text: "" })} sx={{ textTransform: "none" }}>Annulla</Button>
          <Button color="error" variant="contained" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
            onClick={async () => { const id = confirmDel.id; setConfirmDel({ open: false, id: null, text: "" }); await deleteNote(id); }}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open} autoHideDuration={2400}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Shell>
  );
}
